/** Embedded metadata prefix for inline call SYSTEM messages in chat threads. */
export const CALL_SYSTEM_META_PREFIX = '\u0000ASCEND_CALL:'

export type CallSystemMeta = {
  v: 1
  callId?: string
  callPurpose?: 'normal' | 'on-call' | 'urgent'
  urgentLocation?: { lat: number; lng: number; label?: string; accuracy?: number } | null
  mediaType?: 'voice' | 'video'
  status?: string
  /**
   * Discriminates the inline tile variant. Absent = a call-summary tile (the
   * legacy/default: completed/missed/declined call recap). 'callback-request' is
   * a lightweight in-thread "please call me back" prompt posted by the caller
   * when the status gate BLOCKS a 1:1 call. This is intentionally distinct from
   * the Sessions "book a callback session" transfer feature. 'location-share' is
   * an in-call "I shared my location" entry posted to the direct thread so both
   * parties keep a record (rendered directionally per viewer).
   */
  kind?: 'callback-request' | 'location-share'
  /** For 'callback-request': the user who asked to be called back. */
  requesterId?: string
  /** For 'location-share': the user who shared their location. */
  sharerId?: string
  /** For 'location-share': display name of the user who shared (recipient copy). */
  sharerName?: string
}

/**
 * Build the SYSTEM-message body for an in-thread callback request. Posted via the
 * normal message-send path (no schema/resolver change). The display text is what
 * renders if an older client can't parse the meta.
 */
export function encodeCallbackRequestBody(input: {
  requesterId: string
  requesterName: string
}): string {
  const meta: CallSystemMeta = {
    v: 1,
    kind: 'callback-request',
    requesterId: input.requesterId,
  }
  return encodeCallSystemBody(meta, `📞 ${input.requesterName} requested a callback.`)
}

/**
 * Build the SYSTEM-message body for an in-call location share. Posted to the
 * direct thread via the normal message-send path so it appears in BOTH parties'
 * conversation/activity record. The tile renders directionally (see
 * CallSystemMessageTile); the display text is the recipient-facing fallback for
 * older clients and thread/notification previews.
 */
export function encodeLocationShareBody(input: {
  sharerId: string
  sharerName: string
  location: { lat: number; lng: number; label?: string; accuracy?: number }
}): string {
  const meta: CallSystemMeta = {
    v: 1,
    kind: 'location-share',
    sharerId: input.sharerId,
    sharerName: input.sharerName,
    urgentLocation: input.location,
  }
  return encodeCallSystemBody(meta, `📍 ${input.sharerName} shared their location`)
}

export function encodeCallSystemBody(meta: CallSystemMeta, displayText: string): string {
  return `${CALL_SYSTEM_META_PREFIX}${JSON.stringify(meta)}\n${displayText}`
}

export function parseCallSystemBody(body: string): {
  meta: CallSystemMeta | null
  displayText: string
} {
  if (!body.startsWith(CALL_SYSTEM_META_PREFIX)) {
    return { meta: null, displayText: body }
  }
  const newline = body.indexOf('\n')
  if (newline < 0) return { meta: null, displayText: body }
  const json = body.slice(CALL_SYSTEM_META_PREFIX.length, newline)
  try {
    const meta = JSON.parse(json) as CallSystemMeta
    if (meta?.v !== 1) return { meta: null, displayText: body.slice(newline + 1) }
    return { meta, displayText: body.slice(newline + 1) }
  } catch {
    return { meta: null, displayText: body }
  }
}

/** The marker without the leading NUL byte (some stores strip it from snippets). */
const CALL_SYSTEM_MARKER = CALL_SYSTEM_META_PREFIX.slice(1)

/**
 * When a call ends, the same callId is written to call history and as an inline
 * SYSTEM message in the originating thread. Returns that callId for terminal call
 * recap messages so the activity feed can show one row, not two. Callback-request
 * tiles are not terminal recaps and return null.
 */
export function extractTerminalCallSystemCallId(body: string | null | undefined): string | null {
  if (!body) return null
  const markerIdx = body.indexOf(CALL_SYSTEM_MARKER)
  if (markerIdx < 0) return null
  const canonical =
    CALL_SYSTEM_META_PREFIX + body.slice(markerIdx + CALL_SYSTEM_MARKER.length)
  const { meta } = parseCallSystemBody(canonical)
  if (!meta || meta.kind === 'callback-request' || meta.kind === 'location-share') return null
  return meta.callId?.trim() || null
}

/**
 * Short, human-readable thread/sidebar (and notifications) preview for an inline
 * call SYSTEM message. Returns null when `body` is not a call system message, so
 * callers can fall through to normal text handling. Reuses the same decoder as
 * CallSystemMessageTile so previews and tiles stay consistent. Tolerates a
 * stripped leading NUL byte and truncated bodies so raw JSON never leaks.
 */
export function callSystemThreadPreview(body: string): string | null {
  if (!body) return null
  const markerIdx = body.indexOf(CALL_SYSTEM_MARKER)
  if (markerIdx < 0) return null
  const canonical =
    CALL_SYSTEM_META_PREFIX + body.slice(markerIdx + CALL_SYSTEM_MARKER.length)
  const { meta, displayText } = parseCallSystemBody(canonical)
  if (meta?.kind === 'callback-request') return '📞 Callback requested'
  if (meta?.kind === 'location-share') return '📍 Location shared'
  const text = displayText.trim()
  if (!text || text.includes(CALL_SYSTEM_MARKER)) return '📞 Call'
  return text
}

export function callSystemDisplayText(input: {
  status: string
  mediaType?: 'voice' | 'video' | null
  callPurpose?: 'normal' | 'on-call' | 'urgent' | null
  startedAt: string
  endedAt: string
  formatDuration: (start: string, end: string) => string | null
}): string {
  const { status, mediaType, callPurpose, startedAt, endedAt, formatDuration } = input
  const isVideo = mediaType === 'video'
  const urgent = callPurpose === 'urgent'
  const onCall = callPurpose === 'on-call'

  if (status === 'COMPLETED' || status === 'INTERRUPTED') {
    const duration = formatDuration(startedAt, endedAt)
    if (urgent) return duration ? `🚨 Urgent help call · ${duration}` : '🚨 Urgent help call'
    if (onCall) return duration ? `🟠 On-call · ${duration}` : '🟠 On-call'
    if (isVideo) return duration ? `📹 Video call · ${duration}` : '📹 Video call'
    return duration ? `📞 Voice call · ${duration}` : '📞 Voice call'
  }
  if (status === 'DECLINED') {
    if (urgent) return '🚨 Urgent call declined'
    if (onCall) return '🟠 On-call call declined'
    return '📞 Call declined'
  }
  if (status === 'BUSY') return urgent ? '🚨 Urgent call missed (busy)' : '📞 Call missed (busy)'
  if (urgent) return '🚨 Missed urgent call'
  if (onCall) return '🟠 Missed on-call call'
  return '📞 Missed call'
}
